import React, { useEffect, useState, useCallback } from 'react'
import { Button } from '../../../components/ui/button'
import { Input } from '../../../components/ui/input'
import { Label } from '../../../components/ui/label'
import { useUserRole } from '../../../context/UserRoleProvider'
import { api, apiRequest } from '../../../lib/api'

export default function Team() {
  const { getParishAdminParishId } = useUserRole()

  const [invites, setInvites] = useState([])
  const [members, setMembers] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [parishId, setParishId] = useState(null)

  const [inviteEmail, setInviteEmail] = useState('')
  const [inviteRole, setInviteRole] = useState('user')
  const [inviteSubmitting, setInviteSubmitting] = useState(false)
  const [inviteError, setInviteError] = useState('')

  const resolveParishId = async () => {
    let id = getParishAdminParishId()
    if (!id) {
      const userData = await apiRequest('/api/v1/auth/me')
      const roleDetails = userData?.roleDetails || []
      id = roleDetails.find((r) => r.name === 'parish-admin')?.parishId
    }
    return id
  }

  const loadData = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const id = await resolveParishId()
      if (!id) {
        setError('You are not associated with a parish as a parish admin.')
        setLoading(false)
        return
      }
      setParishId(id)
      const [invitesResult, membersResult] = await Promise.all([
        api.listParishInvites(id),
        api.listParishMembers(id),
      ])
      setInvites(invitesResult)
      setMembers(membersResult)
    } catch (err) {
      setError(err.message || 'Failed to load team data')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    loadData()
  }, [loadData])

  const handleInviteSubmit = async (e) => {
    e.preventDefault()
    setInviteError('')
    setInviteSubmitting(true)
    try {
      await api.createParishInvite(parishId, { email: inviteEmail, role: inviteRole })
      setInviteEmail('')
      setInviteRole('user')
      await loadData()
    } catch (err) {
      setInviteError(err.message || 'Failed to send invite')
    } finally {
      setInviteSubmitting(false)
    }
  }

  const handleRevoke = async (inviteId) => {
    try {
      await api.revokeParishInvite(parishId, inviteId)
      await loadData()
    } catch (err) {
      setError(err.message || 'Failed to revoke invite')
    }
  }

  const handleResend = async (inviteId) => {
    try {
      await api.resendParishInvite(parishId, inviteId)
      await loadData()
    } catch (err) {
      setError(err.message || 'Failed to resend invite')
    }
  }

  const handleRoleChange = async (userId, role) => {
    try {
      await api.updateParishMemberRole(parishId, userId, role)
      await loadData()
    } catch (err) {
      setError(err.message || 'Failed to update role')
    }
  }

  const adminCount = members.filter((m) => m.role === 'parish-admin').length

  if (loading) {
    return <div className="p-8 text-charcoal-muted">Loading...</div>
  }

  return (
    <div className="p-8 max-w-4xl mx-auto space-y-10">
      <div>
        <h1 className="text-2xl font-semibold mb-1">Team</h1>
        <p className="text-charcoal-muted">Invite people to your parish and manage admin access.</p>
      </div>

      {error && <div className="text-red-600 text-sm">{error}</div>}

      <section>
        <h2 className="text-lg font-medium mb-4">Invite someone</h2>
        <form onSubmit={handleInviteSubmit} className="flex flex-wrap items-end gap-4">
          <div>
            <Label htmlFor="invite-email" className="mb-2 block">Email</Label>
            <Input
              id="invite-email"
              type="email"
              required
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              placeholder="person@example.com"
            />
          </div>
          <div>
            <Label htmlFor="invite-role" className="mb-2 block">Role</Label>
            <select
              id="invite-role"
              className="border rounded-md px-3 py-2"
              value={inviteRole}
              onChange={(e) => setInviteRole(e.target.value)}
            >
              <option value="user">User</option>
              <option value="parish-admin">Parish Admin</option>
            </select>
          </div>
          <Button
            type="submit"
            disabled={inviteSubmitting || !inviteEmail}
            className="bg-gold text-black border-gold hover:bg-gold/90"
          >
            {inviteSubmitting ? 'Sending...' : 'Send invite'}
          </Button>
        </form>
        {inviteError && <div className="text-red-600 text-sm mt-2">{inviteError}</div>}
      </section>

      <section>
        <h2 className="text-lg font-medium mb-4">Pending invites</h2>
        {invites.length === 0 ? (
          <p className="text-charcoal-muted text-sm">No pending invites.</p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-charcoal-muted border-b">
                <th className="py-2">Email</th>
                <th className="py-2">Role</th>
                <th className="py-2">Expires</th>
                <th className="py-2"></th>
              </tr>
            </thead>
            <tbody>
              {invites.map((invite) => (
                <tr key={invite.id} className="border-b">
                  <td className="py-2">{invite.email}</td>
                  <td className="py-2">{invite.role}</td>
                  <td className="py-2">{new Date(invite.expiresAt).toLocaleDateString()}</td>
                  <td className="py-2 space-x-2 text-right">
                    <Button variant="outline" size="sm" onClick={() => handleResend(invite.id)}>
                      Resend
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => handleRevoke(invite.id)}>
                      Revoke
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      <section>
        <h2 className="text-lg font-medium mb-4">Members</h2>
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-charcoal-muted border-b">
              <th className="py-2">Name</th>
              <th className="py-2">Email</th>
              <th className="py-2">Role</th>
              <th className="py-2"></th>
            </tr>
          </thead>
          <tbody>
            {members.map((member) => {
              const isLastAdmin = member.role === 'parish-admin' && adminCount <= 1
              return (
                <tr key={member.userId} className="border-b">
                  <td className="py-2">{member.name}</td>
                  <td className="py-2">{member.email}</td>
                  <td className="py-2">{member.role}</td>
                  <td className="py-2 text-right">
                    {member.role === 'parish-admin' ? (
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={isLastAdmin}
                        title={isLastAdmin ? 'A parish needs at least one admin' : undefined}
                        onClick={() => handleRoleChange(member.userId, 'user')}
                      >
                        Remove admin
                      </Button>
                    ) : (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleRoleChange(member.userId, 'parish-admin')}
                      >
                        Make admin
                      </Button>
                    )}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </section>
    </div>
  )
}
